/**
 * BPR diagram-spec contracts (the visual output layer)
 * -----------------------------------------------------
 * Zod schemas for the render-agnostic diagram specs the LLM emits during BPR
 * synthesis: the pinned System Architecture pair (current + future state) and
 * one left-to-right workflow diagram per functional area. The LLM authors
 * TOPOLOGY ONLY (nodes/edges/labels/status) - never coordinates or JSX; the
 * frontend lays the spec out at render time (elkjs) and draws it (React Flow).
 *
 * Specs persist through the EXISTING BPR deliverables path: one
 * sales.bpr_output row with kind 'diagrams' per generation run, whose
 * `content` is bprDiagramsOutputContentSchema ({ specs: [...] }) - versioned
 * exactly like the SOW/estimate/resource-plan outputs (integer version,
 * prior rows flipped 'superseded'). `source_refs` records the
 * bpr_graph_version the batch was synthesized from, so diagrams tie back to
 * the inverted-triangle output - node `issues` entries carry the graph's
 * finding ids/summaries.
 *
 * Mirrors bpr-graph.ts layering: permissive structural schemas + a
 * validateBprDiagramSpec() rule check wired into the strict schema so
 * generateValidated's retry loop feeds violations back to the model.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Value vocabularies
// ---------------------------------------------------------------------------
export const BPR_DIAGRAM_KINDS = ['system-architecture', 'workflow'];
export const bprDiagramKindSchema = z.enum(BPR_DIAGRAM_KINDS);
/**
 * Architecture diagrams always carry a state. Workflow diagrams carry
 * 'current' or 'future' too (each questionnaire section renders a
 * current-state process flow with a future-state tab); null is tolerated on
 * workflows for batches generated before future-state workflows existed and
 * means current-state.
 */
export const BPR_DIAGRAM_STATES = ['current', 'future'];
export const bprDiagramStateSchema = z.enum(BPR_DIAGRAM_STATES);
export const BPR_DIAGRAM_NODE_TYPES = [
    'system',
    'process-step',
    'actor',
    'datastore',
    'external',
];
export const bprDiagramNodeTypeSchema = z.enum(BPR_DIAGRAM_NODE_TYPES);
export const BPR_DIAGRAM_NODE_STATUSES = ['existing', 'new', 'changed', 'removed'];
export const bprDiagramNodeStatusSchema = z.enum(BPR_DIAGRAM_NODE_STATUSES);
export const BPR_DIAGRAM_EDGE_KINDS = ['integration', 'manual-handoff', 'data-flow'];
export const bprDiagramEdgeKindSchema = z.enum(BPR_DIAGRAM_EDGE_KINDS);
export const BPR_DIAGRAM_EDGE_STATUSES = ['existing', 'new', 'changed'];
export const bprDiagramEdgeStatusSchema = z.enum(BPR_DIAGRAM_EDGE_STATUSES);
// ---------------------------------------------------------------------------
// The diagram spec (the LLM output contract)
// ---------------------------------------------------------------------------
export const bprDiagramNodeSchema = z.object({
    /** Stable slug id, unique within the diagram (e.g. 'order-intake-edi'). */
    id: z.string().min(1),
    label: z.string().min(1),
    type: bprDiagramNodeTypeSchema,
    /** Owning system, e.g. 'NetSuite', 'Salesforce', 'Excel'. */
    system: z.string().min(1),
    status: bprDiagramNodeStatusSchema,
    /** Parsed issue annotations, e.g. 'finding.manual_billing_excel: invoices built by hand'. */
    issues: z.array(z.string()),
});
export const bprDiagramEdgeSchema = z.object({
    from: z.string().min(1),
    to: z.string().min(1),
    /** What flows, e.g. 'Sales orders', 'Invoice'. */
    label: z.string(),
    kind: bprDiagramEdgeKindSchema,
    status: bprDiagramEdgeStatusSchema,
});
export const bprDiagramSpecSchema = z.object({
    kind: bprDiagramKindSchema,
    title: z.string().min(1),
    /** 'current' | 'future' for system-architecture; null for workflows. */
    state: bprDiagramStateSchema.nullable(),
    nodes: z.array(bprDiagramNodeSchema),
    edges: z.array(bprDiagramEdgeSchema),
});
// ---------------------------------------------------------------------------
// Rule validation (wired into the model retry loop)
// ---------------------------------------------------------------------------
/**
 * Label budgets, enforced at GENERATION time only (validateBprDiagramSpec /
 * the strict schema) - deliberately NOT on the permissive parse schemas, so
 * stored batches from before the cap keep loading (the renderer clamps long
 * labels to two lines as the fallback). 60 chars = two label lines in the
 * fixed diagram-node footprint; 40 chars fits an edge label in a layer gap.
 */
export const BPR_DIAGRAM_NODE_LABEL_MAX = 60;
export const BPR_DIAGRAM_EDGE_LABEL_MAX = 40;
/** Cross-node rules the structural schema cannot express. */
export function validateBprDiagramSpec(spec) {
    const issues = [];
    const ids = new Set();
    for (const node of spec.nodes) {
        if (ids.has(node.id)) {
            issues.push({ rule: 'duplicate_node_id', message: `duplicate node id: ${node.id}`, at: node.id });
        }
        ids.add(node.id);
        if (node.label.length > BPR_DIAGRAM_NODE_LABEL_MAX) {
            issues.push({
                rule: 'label_too_long',
                message: `node ${node.id} label exceeds ${BPR_DIAGRAM_NODE_LABEL_MAX} characters (${node.label.length}) - shorten to 2-5 words`,
                at: node.id,
            });
        }
    }
    if (spec.nodes.length === 0) {
        issues.push({ rule: 'empty_diagram', message: 'diagram has no nodes' });
    }
    const seenEdges = new Set();
    for (const edge of spec.edges) {
        const at = `${edge.from}->${edge.to}(${edge.label})`;
        if (!ids.has(edge.from)) {
            issues.push({ rule: 'edge_endpoint_unresolved', message: `edge ${at}: 'from' is not a node id`, at });
        }
        if (!ids.has(edge.to)) {
            issues.push({ rule: 'edge_endpoint_unresolved', message: `edge ${at}: 'to' is not a node id`, at });
        }
        if (edge.from === edge.to) {
            issues.push({ rule: 'self_edge', message: `self-referencing edge: ${at}`, at });
        }
        const key = `${edge.from}|${edge.to}|${edge.label}`;
        if (seenEdges.has(key)) {
            issues.push({ rule: 'duplicate_edge', message: `duplicate edge: ${at}`, at });
        }
        seenEdges.add(key);
        if (edge.label.length > BPR_DIAGRAM_EDGE_LABEL_MAX) {
            issues.push({
                rule: 'label_too_long',
                message: `edge ${at} label exceeds ${BPR_DIAGRAM_EDGE_LABEL_MAX} characters (${edge.label.length}) - name what moves in 1-4 words`,
                at,
            });
        }
    }
    if (spec.kind === 'system-architecture' && spec.state === null) {
        issues.push({
            rule: 'state_kind_mismatch',
            message: "system-architecture diagrams must set state 'current' or 'future'",
        });
    }
    // A current-state diagram cannot contain target-landscape markers.
    if (spec.state === 'current') {
        for (const node of spec.nodes) {
            if (node.status === 'new' || node.status === 'removed') {
                issues.push({
                    rule: 'status_state_mismatch',
                    message: `current-state node ${node.id} cannot be '${node.status}' - mark additions/removals on the future-state diagram`,
                    at: node.id,
                });
            }
        }
        for (const edge of spec.edges) {
            if (edge.status === 'new') {
                issues.push({
                    rule: 'status_state_mismatch',
                    message: `current-state edge ${edge.from}->${edge.to} cannot be 'new'`,
                    at: `${edge.from}->${edge.to}`,
                });
            }
        }
    }
    return issues;
}
/** Strict schema for the model retry loop: shape + the cross-node rules. */
export const bprDiagramSpecStrictSchema = bprDiagramSpecSchema.superRefine((spec, ctx) => {
    for (const issue of validateBprDiagramSpec(spec)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `[${issue.rule}] ${issue.message}`,
            path: issue.at ? ['(diagram)', issue.at] : ['(diagram)'],
        });
    }
});
/**
 * Pair envelope tolerance: light/fast models occasionally emit the pair as a
 * 2-tuple `[current, future]` instead of `{ current, future }` - re-shape it
 * before validation so the retry loop is spent on real topology errors.
 */
function coercePairEnvelope(value) {
    if (Array.isArray(value) && value.length === 2) {
        return { current: value[0], future: value[1] };
    }
    return value;
}
/** The architecture pair is generated in ONE call so the two states stay coherent. */
export const bprArchitecturePairSchema = z.preprocess(coercePairEnvelope, z
    .object({
    current: bprDiagramSpecStrictSchema,
    future: bprDiagramSpecStrictSchema,
})
    .superRefine((pair, ctx) => {
    // Each slot must carry ITS state - a future-state spec in the current
    // slot (or vice versa) is a mis-slotted generation, not a valid pair.
    if (pair.current.kind !== 'system-architecture' || pair.current.state !== 'current') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "[pair_slot_mismatch] 'current' must be a system-architecture spec with state 'current'",
            path: ['current'],
        });
    }
    if (pair.future.kind !== 'system-architecture' || pair.future.state !== 'future') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "[pair_slot_mismatch] 'future' must be a system-architecture spec with state 'future'",
            path: ['future'],
        });
    }
}));
/**
 * A section's workflow pair is generated in ONE call so the future-state
 * process flow stays a coherent evolution of the current one (same node ids
 * where a step carries over).
 */
export const bprWorkflowPairSchema = z.preprocess(coercePairEnvelope, z
    .object({
    current: bprDiagramSpecStrictSchema,
    future: bprDiagramSpecStrictSchema,
})
    .superRefine((pair, ctx) => {
    if (pair.current.kind !== 'workflow' || pair.current.state !== 'current') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "[pair_slot_mismatch] 'current' must be a workflow spec with state 'current'",
            path: ['current'],
        });
    }
    if (pair.future.kind !== 'workflow' || pair.future.state !== 'future') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "[pair_slot_mismatch] 'future' must be a workflow spec with state 'future'",
            path: ['future'],
        });
    }
}));
// ---------------------------------------------------------------------------
// Persisted content shape (bpr_output.content for kind 'diagrams')
// ---------------------------------------------------------------------------
/** One diagram of a generation run, as stored inside the output row. */
export const bprDiagramsOutputEntrySchema = z.object({
    kind: bprDiagramKindSchema,
    /** Functional-area key (graph functional node slug) for workflows; null for architecture. */
    functionalAreaKey: z.string().nullable().optional(),
    state: bprDiagramStateSchema.nullable().optional(),
    title: z.string(),
    spec: bprDiagramSpecSchema,
});
/** The `content` payload of a sales.bpr_output row with kind 'diagrams'. */
export const bprDiagramsOutputContentSchema = z.object({
    specs: z.array(bprDiagramsOutputEntrySchema).min(1),
});
// ---------------------------------------------------------------------------
// DTOs + request contracts (API wire shapes)
// ---------------------------------------------------------------------------
export const bprDiagramRecordSchema = z.object({
    /** Synthetic per-diagram id ('<output uuid>:<index>') - stable within a batch. */
    uuid: z.string(),
    engagementUuid: z.string().uuid().nullable().optional(),
    /** The bpr_graph_version the batch was synthesized from. */
    graphVersionUuid: z.string().uuid().nullable().optional(),
    /** The bpr_output row (kind 'diagrams') the batch lives in; reads return the newest. */
    batchUuid: z.string().uuid(),
    kind: bprDiagramKindSchema,
    /** Functional-area key for workflow diagrams; null for architecture. */
    functionalAreaKey: z.string().nullable().optional(),
    state: bprDiagramStateSchema.nullable().optional(),
    title: z.string(),
    spec: bprDiagramSpecSchema,
    modelRef: z.string().nullable().optional(),
    promptRef: z.string().nullable().optional(),
    generatedAt: z.string().nullable().optional(),
    createdAt: z.string(),
});
export const bprGenerateDiagramsRequestSchema = z.object({
    /** Regenerate even when the latest batch already covers the latest graph version. */
    force: z.boolean().optional(),
});
/**
 * Refresh ONLY the future-state workflow specs of the named questionnaire
 * sections (after the user saved changes in them); every other spec of the
 * newest batch carries forward unchanged into the new version.
 */
export const bprRefreshSectionDiagramsRequestSchema = z.object({
    sectionKeys: z.array(z.string().min(1)).min(1).max(24),
});
/**
 * Create the process-flow pairs for the named questionnaire sections when
 * they are MISSING from the newest batch (their questions were captured
 * after the batch was minted); every existing spec - the architecture pair
 * included - carries forward unchanged. Sections already covered are
 * skipped server-side.
 */
export const bprEnsureSectionDiagramsRequestSchema = z.object({
    sectionKeys: z.array(z.string().min(1)).min(1).max(24),
});
//# sourceMappingURL=bpr-diagram.js.map